import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as UserData from "../data/userData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

// The password values are stored in a child observable in the view model in order to
// allow rebinding them after save.  This allows clearing out the values without the validation
// errors showing.
interface IPasswordValues {
    CurrentPassword: KnockoutObservable<string>;
    NewPassword: KnockoutObservable<string>;
    NewPasswordConfirm: KnockoutObservable<string>;
}

interface IChangePasswordViewModel {
    UserID: KnockoutObservable<number>;
    PasswordValues: KnockoutObservable<IPasswordValues>;
    MinimumPasswordLength: KnockoutObservable<number>;
    PasswordComplexityRequirements: KnockoutObservable<string>;
    isValid: () => boolean;
    errors?: KnockoutValidationErrors;
}

export class ChangePasswordController implements IController {
    private container: JQuery;
    private viewModel: IChangePasswordViewModel;
    private passwordRequirements: UserData.IPasswordRequirementsResponse;

    public save(): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let waitContainer: JQuery;
        const app: IApplication = Global.app();

        if (this.viewModel.isValid()) {

            waitContainer = UI.showWaitIndicator(this.container);

            const request: UserData.IChangePasswordRequest = {
                CurrentPassword: this.viewModel.PasswordValues().CurrentPassword(),
                NewPassword: this.viewModel.PasswordValues().NewPassword(),
                NewPasswordConfirm: this.viewModel.PasswordValues().NewPasswordConfirm(),
            };

            UserData.ChangePassword(request)
                .done((response: Data.IResponse<UserData.IChangePasswordResponse>) => {
                    if (response.Success) {
                        UI.showNotificationMessage(Constants.PHRASE_PASSWORDCHANGED, UI.messageTypeValues.success);
                        deferred.resolve();
                    } else {
                        if (response.Payload.ShouldReauthenticate) {
                            app.pageManager().logout();
                            UI.showErrorMessage([Constants.PHRASE_CHANGEPASSWORDACCOUNTLOCKED]);
                            location.href = "/";
                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    this.viewModel.PasswordValues(this.createPasswordValuesModel());
                    UI.hideWaitIndicator(this.container, waitContainer);
                });

        } else {
            deferred.reject();
        }

        return deferred.promise();
    }

    public render(container: JQuery<HTMLElement>): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const me = this;

        UserData.GetPasswordRequirements()
            .done((response: Data.IResponse<UserData.IPasswordRequirementsResponse>) => {
                if (response.Success) {
                    me.passwordRequirements = response.Payload;
                    me.setupAndBindViewModel();
                }
            });

        deferred.resolve();
        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        return false;
    }

    public appRouterID(params: any[]): string {
        return "ChangePassword";
    }

    public title(): string {
        return "Change Password";
    }

    public subTitle(): string {
        return "";
    }

    private mustEqual(val1: string, val2: string): boolean {
        return val1 === val2;
    }

    private setupAndBindViewModel() {

        this.viewModel = {
            UserID: ko.observable<number>(),
            PasswordValues: ko.observable<IPasswordValues>(this.createPasswordValuesModel()),
            MinimumPasswordLength: ko.observable<number>(this.passwordRequirements.MinimumPasswordLength),
            PasswordComplexityRequirements: ko.observable<string>(this.passwordRequirements.PasswordComplexityRequirements),
            isValid: (): boolean => {
                if (this.viewModel.errors && this.viewModel.errors().length > 0) {
                    this.viewModel.errors.showAllMessages();
                    UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
                    return false;
                }
                return true;
            },
        };

        this.viewModel.errors = ko.validation.group(this.viewModel);

        ko.applyBindings(this.viewModel, this.container[0]);
    }

    private createPasswordValuesModel(): IPasswordValues {
        const passwordValues: IPasswordValues = {
            CurrentPassword: ko.observable<string>().extend({ required: true }),
            NewPassword: ko.observable<string>().extend({ required: true, minLength: this.passwordRequirements.MinimumPasswordLength }),
            NewPasswordConfirm: ko.observable<string>(),
        };

        // Need to set up validation for this field separately because it depends on NewPassword already existing
        passwordValues.NewPasswordConfirm = ko.observable<string>().extend({
            required: true,
            minLength: this.passwordRequirements.MinimumPasswordLength,
            validation: {
                validator: this.mustEqual,
                message: "Passwords do not match.",
                params: passwordValues.NewPassword,
            },
        });
        return passwordValues;

    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/changePassword.ts